import React from 'react';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import '../EtlPages/etlMobile.css';

/**
 * Phone primitives for ETL Configuration (≤768px).
 *
 * The desktop tables stay as they are; below the useIsMobile cut each tab
 * swaps its table for a card list built from these, and per-row icon
 * buttons move into a bottom sheet of 48px rows. Styles: etlMobile.css.
 */

/** Full-width search over a horizontally scrolling chip row. */
export function MobileFilterBar({ search, children, bar }) {
  return (
    <div className="ep-m ep-m-filters">
      {search ? (
        <div className="ep-m-search">
          <Search size={16} className="ep-m-search-icon" aria-hidden="true" />
          <input
            type="search"
            value={search.value || ''}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder || 'Search…'}
            aria-label={search.placeholder || 'Search'}
            enterKeyHint="search"
          />
          {search.value ? (
            <button type="button" className="ep-m-search-clear" onClick={() => search.onChange('')} aria-label="Clear search">
              <X size={16} />
            </button>
          ) : null}
        </div>
      ) : null}
      {React.Children.count(children) ? <div className="ep-m-chips" role="group" aria-label="Filters">{children}</div> : null}
      {bar ? <div className="ep-m-bar">{bar}</div> : null}
    </div>
  );
}

/**
 * A filter as a chip: a native <select> so the phone's own picker opens.
 * value null/'' = no filter (shows `placeholder`).
 */
export function ChipSelect({ value, options = [], onChange, placeholder, ariaLabel }) {
  const active = value != null && value !== '';
  return (
    <label className={`ep-m-chip-select${active ? ' ep-active' : ''}`}>
      <select
        value={active ? String(value) : ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        aria-label={ariaLabel || placeholder}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
      </select>
      <ChevronDown size={14} aria-hidden="true" />
    </label>
  );
}

/** Eyebrow + count over a bordered list of cards. */
export function MobileGroup({ label, count, warn, children }) {
  return (
    <section className="ep-m-group" aria-label={typeof label === 'string' ? label : undefined}>
      {label ? (
        <h3 className={`ep-m-eyebrow${warn ? ' ep-warn' : ''}`}>
          <span className="ep-m-eyebrow-label">{label}</span>
          {count != null ? <span className="ep-m-eyebrow-n">{count}</span> : null}
        </h3>
      ) : null}
      <div className="ep-m-list">{children}</div>
    </section>
  );
}

/**
 * One tappable row: [tinted icon chip] title / sub … [trailing] [chevron].
 * `icon` is a component; `tone` is a run-state tone (ok · warn · error · info · busy · muted).
 */
export function MobileCard({
  icon: Icon, tone, title, titleClassName, sub, subEnd, trailing, selected, onClick, ariaLabel,
}) {
  return (
    <button
      type="button"
      className={`ep-m-card${selected ? ' ep-selected' : ''}`}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {Icon ? (
        <span className={`ep-m-chip-icon${tone ? ` ep-tone-${tone}` : ''}`} aria-hidden="true">
          <Icon size={18} />
        </span>
      ) : null}
      <span className="ep-m-card-body">
        <span className="ep-m-card-top">
          <span className={`ep-m-title${titleClassName ? ` ${titleClassName}` : ''}`}>{title}</span>
          {trailing}
        </span>
        {(sub || subEnd) ? (
          <span className="ep-m-sub">
            {typeof sub === 'string' ? <span className="ep-m-sub-text">{sub}</span> : sub}
            {subEnd ? <span className="ep-m-sub-end">{subEnd}</span> : null}
          </span>
        ) : null}
      </span>
      <ChevronRight size={18} className="ep-m-chev" aria-hidden="true" />
    </button>
  );
}

/** "Show N more" footer for long card lists. */
export function MobileMore({ shown, total, step = 40, onMore }) {
  if (shown >= total) return null;
  return (
    <button type="button" className="ep-m-more" onClick={() => onMore(Math.min(total, shown + step))}>
      Show {Math.min(step, total - shown)} more · {shown} of {total}
    </button>
  );
}

/**
 * Bottom sheet (Radix dialog, side="bottom").
 *
 * Radix makes everything outside the sheet inert while it is open, so any
 * action that opens one of the page's own modals must close the sheet first
 * — callers do that before invoking the handler.
 */
export function ActionSheet({ open, onOpenChange, title, titleClassName, subtitle, meta, children }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" hideClose className="ep-m-sheet">
        <span aria-hidden="true" className="ep-m-grabber" />
        <div className="ep-m-sheet-head">
          <SheetTitle className={`ep-m-sheet-title${titleClassName ? ` ${titleClassName}` : ''}`}>{title}</SheetTitle>
          {subtitle ? (
            <SheetDescription className="ep-m-sheet-sub">{subtitle}</SheetDescription>
          ) : (
            <SheetDescription className="sr-only">Actions</SheetDescription>
          )}
          {meta ? <div className="ep-m-sheet-meta">{meta}</div> : null}
        </div>
        {children}
      </SheetContent>
    </Sheet>
  );
}

/** Key/value rows inside a sheet. rows: [{ label, value }] — empty values are skipped. */
export function SheetKv({ rows = [] }) {
  const shown = rows.filter((r) => r && r.value != null && r.value !== '');
  if (!shown.length) return null;
  return (
    <dl className="ep-m-kv">
      {shown.map((r) => (
        <div key={r.label}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A group of 48px action rows. */
export function SheetActions({ children, label }) {
  return <div className="ep-m-actions" role="group" aria-label={label}>{children}</div>;
}

/** One 48px action row. tone: 'primary' | 'danger' | undefined. */
export function SheetAction({ icon: Icon, tone, hint, children, ...rest }) {
  return (
    <button type="button" className={`ep-m-action${tone ? ` ep-${tone}` : ''}`} {...rest}>
      {Icon ? <Icon size={18} aria-hidden="true" /> : null}
      <span className="ep-m-action-label">{children}</span>
      {hint ? <span className="ep-m-action-hint">{hint}</span> : null}
    </button>
  );
}

// ── Formatting helpers shared by the card lists ─────────────────────────

/** "14:05" in the viewer's locale, or null. */
export function clockTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

/** "3h ago" style relative time, or null. */
export function timeAgo(value) {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
}

/** Email / SFTP from whatever arrival hint a record carries. */
export function arrivalLabel(hint) {
  if (!hint) return null;
  return /sftp/i.test(String(hint)) ? 'SFTP' : 'Email';
}
